import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/services/supabase/supabase';
import { useToast } from '@/components/ui/use-toast';
import { invalidateTenantQueries } from '@/services/query/tenant-queries';

interface SetupProjectParams {
  projectName: string;
  projectDescription?: string;
  storyName: string;
  storyDescription?: string;
  categories: string[];
}

interface SetupProjectResult {
  project: {
    id: string;
    name: string;
    description: string | null;
  };
  story: {
    id: string;
    name: string;
    description: string | null;
  };
  categories: Array<{
    id: string;
    name: string;
    template_id: string;
  }>;
}

/**
 * Hook for setting up a new project with a story and template categories
 * Uses the setup_new_project database function
 */
export function useSetupProject() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation<SetupProjectResult, Error, SetupProjectParams>({
    mutationFn: async ({
      projectName,
      projectDescription,
      storyName,
      storyDescription,
      categories,
    }: SetupProjectParams) => {
      // Call the setup_new_project function
      const { data, error } = await supabase.rpc('setup_new_project', {
        p_project_name: projectName,
        p_story_name: storyName,
        p_project_description: projectDescription || null,
        p_story_description: storyDescription || null,
        p_categories: categories,
      });

      if (error) {
        console.error('Error setting up project:', error);
        throw new Error(`Failed to set up project: ${error.message}`);
      }

      return data as SetupProjectResult;
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['projects'] });

      // Invalidate tenant cache since project setup might affect tenant context
      invalidateTenantQueries(queryClient);

      toast({
        title: 'Project created successfully',
        description: 'Your project has been set up with a story and templates',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to create project',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
